import type { ChapterDef, RunScope, StoryPack } from '../types.js';

export interface RunBounds {
  startSceneIndex: number;
  endSceneIndex: number;
}

export function getChapterForSceneIndex(story: StoryPack, sceneIndex: number): ChapterDef | undefined {
  const scene = story.scenes[sceneIndex];
  if (!scene?.chapterId) return story.chapters?.[0];
  return story.chapters?.find((c) => c.id === scene.chapterId);
}

export function getRunBounds(story: StoryPack, scope: RunScope, chapterId?: string): RunBounds {
  if (scope === 'full' || !chapterId) {
    return { startSceneIndex: 0, endSceneIndex: story.scenes.length - 1 };
  }
  const ch = story.chapters?.find((c) => c.id === chapterId);
  if (!ch) {
    return { startSceneIndex: 0, endSceneIndex: story.scenes.length - 1 };
  }
  const indices = story.scenes
    .map((s, i) => (s.chapterId === chapterId ? i : -1))
    .filter((i) => i >= 0);
  if (indices.length === 0) {
    return { startSceneIndex: ch.sceneStart, endSceneIndex: ch.sceneEnd };
  }
  return { startSceneIndex: Math.min(...indices), endSceneIndex: Math.max(...indices) };
}

export function isLastSceneInRun(story: StoryPack, sceneIndex: number, bounds: RunBounds): boolean {
  return sceneIndex >= bounds.endSceneIndex;
}

export function isChapterEnd(story: StoryPack, sceneIndex: number): boolean {
  const ch = getChapterForSceneIndex(story, sceneIndex);
  if (!ch) return false;
  const scene = story.scenes[sceneIndex];
  return scene?.chapterId === ch.id && sceneIndex === ch.sceneEnd;
}

export function nextChapterAfter(story: StoryPack, sceneIndex: number): ChapterDef | undefined {
  const current = getChapterForSceneIndex(story, sceneIndex);
  if (!current || !story.chapters) return undefined;
  const order = story.chapters.findIndex((c) => c.id === current.id);
  return story.chapters[order + 1];
}

export function sceneIdsInChapter(story: StoryPack, chapterId: string): string[] {
  return story.scenes.filter((s) => s.chapterId === chapterId).map((s) => s.id);
}
